/**
 * Front-end mirror of the Employee-Ops RBAC model (functions/src/rbac.ts + org.ts).
 *
 * This is the authorization VOCABULARY for the client. It never grants access on
 * its own — the server (custom claims + Firestore rules) is authoritative. The
 * client uses these helpers only to show/hide UI and to pre-validate forms.
 *
 * Roles use the locked 6-tier model:
 *   super_admin > org_admin > event_admin > supervisor > employee > scanner_only
 */

export type AppRole =
  | 'super_admin'
  | 'org_admin'
  | 'event_admin'
  | 'supervisor'
  | 'employee'
  | 'scanner_only';

/** Higher rank = more privilege. Mirrors functions/src/rbac.ts exactly. */
export const RANK: Record<AppRole, number> = {
  super_admin: 5,
  org_admin: 4,
  event_admin: 3,
  supervisor: 2,
  employee: 1,
  scanner_only: 0,
};

/** Ordered most -> least privileged (for dropdowns). */
export const ROLE_ORDER: AppRole[] = [
  'super_admin',
  'org_admin',
  'event_admin',
  'supervisor',
  'employee',
  'scanner_only',
];

/** The single org id, stamped on every new doc. Mirrors functions/src/org.ts. */
export const ORG_ID = 'breck-vipers';

export function isAppRole(v: unknown): v is AppRole {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(RANK, v);
}

export function rankOf(role: AppRole | null | undefined): number {
  return role && isAppRole(role) ? RANK[role] : -1;
}

/** Human-readable role label. */
export function roleLabel(role: AppRole | string | null | undefined): string {
  switch (role) {
    case 'super_admin':
      return 'Super Admin';
    case 'org_admin':
      return 'Org Admin';
    case 'event_admin':
      return 'Event Admin';
    case 'supervisor':
      return 'Supervisor';
    case 'employee':
      return 'Employee';
    case 'scanner_only':
      return 'Scanner Only';
    default:
      return role ? String(role) : 'Unknown';
  }
}

/** roleAtLeast: does `role` meet or exceed the `min` rank? */
export function roleAtLeast(role: AppRole | null | undefined, min: AppRole): boolean {
  return rankOf(role) >= RANK[min];
}

/** Does a permission list satisfy `perm`? `'all'` is the wildcard. */
export function permsHave(perms: string[] | undefined | null, perm: string): boolean {
  if (!perms) return false;
  return perms.includes('all') || perms.includes(perm);
}

/**
 * Roles an actor of `actorRole` is allowed to assign / invite. Everyone may
 * assign strictly-lower-ranked roles; a super_admin may additionally assign
 * super_admin (mirrors the server's superOnSuper exception in setEmployeeRole),
 * otherwise a super_admin could never promote or manage a peer.
 */
export function assignableRoles(actorRole: AppRole | null | undefined): AppRole[] {
  const actorRank = rankOf(actorRole);
  if (actorRole === 'super_admin') return [...ROLE_ORDER];
  return ROLE_ORDER.filter((r) => RANK[r] < actorRank);
}

/**
 * Can an actor manage a target row? Mirrors the server guard: you must strictly
 * outrank the target, EXCEPT a super_admin may manage another super_admin. You
 * can never manage your own row (the server blocks changing your own role).
 */
export function canManageTarget(
  actorRole: AppRole | null | undefined,
  targetRole: AppRole,
  isSelf: boolean
): boolean {
  if (isSelf) return false;
  if (actorRole === 'super_admin') return true;
  return rankOf(actorRole) > rankOf(targetRole);
}
